import * as React from "react";
import { HTMLAttributes } from "react";

import { isChildOfType, nullElement } from "../../../TypingUtils";
import { MultiLineContainer as MultiLine } from "../../MultiLine/MultiLineContainer";
import { MultiLineSidebarContainer as MultiLineSidebar } from "../../MultiLineSidebar/MultiLineSidebarContainer";
import { RemoveRowButtonContainer as RemoveRowButton } from "../../RemoveRowButton/RemoveRowButtonContainer";
import { RowMenuContainer as RowMenu } from "../../RowMenu/RowMenuContainer";
import { Colgroup } from "../Colgroup";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";

import { ActionButtons, FooterStop, Root, SidebarMultilineRoot, Table } from "./Sidebar.styles";

export interface SidebarProps {
    bindingPath: AbsoluteModelPath;
    buttonType?: "RemoveRowButton" | "RowMenu";
    children: React.ReactNode;
    columnWidth: number[];
    doNotCopyPaths: string[];
    setStop: (x: HTMLDivElement | null) => void;
    isList: boolean;
    focusByHotkey?: boolean;
    maxOccurs?: number;
    isReadOnly?: boolean;
    optional: boolean;
    filteredUniqKey: string;
}

export function Sidebar(props: SidebarProps): JSX.Element {
    let multiLine: undefined | JSX.Element;
    let multilineIndex = React.Children.count(props.children);

    const children = React.Children.map(props.children ?? [], (child: React.ReactNode, index: number) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        if (isChildOfType(child, MultiLine)) {
            multiLine = child;
            multilineIndex = index;
            return nullElement;
        }

        return React.cloneElement<HTMLAttributes<HTMLDivElement>>(
            child as React.ReactElement<HTMLAttributes<HTMLDivElement>>,
            {
                style: { width: props.columnWidth[index] },
            }
        );
    });

    return (
        <Root>
            <div>
                <Table>
                    <Colgroup columnWidth={props.columnWidth.slice(0, multilineIndex)} />
                    <tbody>
                        <tr>{children}</tr>
                    </tbody>
                </Table>
                <FooterStop ref={props.setStop} />
            </div>
            {props.buttonType != undefined && (
                <ActionButtons>
                    <MultiLineSidebar
                        bindingPath={props.bindingPath}
                        isList={props.isList}
                        filteredUniqKey={props.filteredUniqKey}>
                        {props.isReadOnly === true ? null : props.buttonType === "RemoveRowButton" ? (
                            <RemoveRowButton optional={props.optional} filteredUniqKey={props.filteredUniqKey} />
                        ) : (
                            <RowMenu
                                bindingPath={props.bindingPath}
                                maxOccurs={props.maxOccurs}
                                doNotCopyPaths={props.doNotCopyPaths}
                                optional={props.optional}
                            />
                        )}
                    </MultiLineSidebar>
                </ActionButtons>
            )}
            {multiLine != undefined && (
                <SidebarMultilineRoot>
                    <Colgroup columnWidth={props.columnWidth.slice(multilineIndex + 1)} />
                    {multiLine}
                </SidebarMultilineRoot>
            )}
        </Root>
    );
}
